import { useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import eventService from "services/eventService";
import uploadService from "services/uploadService";
import { useAuth } from "auth/AuthProvider";

export default function CreateEventDialog({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const defaultAuthor = user?.firstName || "Équipe";

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [files, setFiles] = useState([]);

  // Dates & heures contrôlées
  const [startDate, setStartDate] = useState(""); // "YYYY-MM-DD"
  const [startTime, setStartTime] = useState(""); // "HH:MM"
  const [endDate, setEndDate] = useState(""); // "YYYY-MM-DD"
  const [endTime, setEndTime] = useState(""); // "HH:MM"
  const [allDay, setAllDay] = useState(false); // par défaut journée entière

  const multiDay = !!(startDate && endDate && startDate !== endDate);
  const timeDisabled = allDay || multiDay;

  function onStartDateChange(e) {
    const val = e.target.value;
    setStartDate(val);
    // si aucune date de fin, on copie la même date
    if (!endDate) setEndDate(val);
    // si dates différentes => on force allDay
    if (endDate && endDate !== val) setAllDay(true);
  }

  function onEndDateChange(e) {
    const val = e.target.value;
    setEndDate(val);
    // si dates différentes => on force allDay
    if (startDate && startDate !== val) setAllDay(true);
  }

  function onAllDayChange(e) {
    setAllDay(e.target.checked);
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setSaving(true);
    const fd = new FormData(e.currentTarget);

    const city = fd.get("city")?.toString().trim() || "";
    const address = fd.get("address")?.toString().trim() || "";
    const organizerName = fd.get("organizerName")?.toString().trim() || "";

    try {
      // Construire dates selon règles:
      let startIso: string | null = null;
      let endIso: string | null = null;

      if (startDate) {
        if (timeDisabled) {
          // journée entière (mono ou multi-jour)
          startIso = `${startDate}T00:00`;
          const endD = endDate || startDate;
          endIso = `${endD}T23:59`;
        } else {
          // demi-journée / horaire précis
          startIso = `${startDate}T${startTime || "00:00"}`;
          const endD = endDate || startDate;
          const endT = endTime || startTime || "00:00";
          endIso = `${endD}T${endT}`;
        }
      }

      // 1) créer l'évènement (sans médias)
      const payload = {
        title: fd.get("title")?.toString().trim(),
        description: fd.get("description")?.toString(),
        startDate: startIso ? new Date(startIso) : null,
        endDate: endIso ? new Date(endIso) : null,
        allDay: timeDisabled ? true : fd.get("allDay") === "on", // si multi-jour, on force true
        locationManual: [address, city].filter(Boolean).join(", ") || undefined,
        organizerManual: organizerName || undefined,
        officialUrl: fd.get("officialUrl")?.toString().trim() || undefined,
        email: fd.get("email")?.toString().trim() || undefined,
        price: fd.get("price") ? Number(fd.get("price")) : undefined,
        author: fd.get("author")?.toString().trim() || defaultAuthor,
        media: [],
      };

      const created = await eventService.createEvent(payload);

      // 2) upload médias dans /uploads/events/:slug (si fichiers)
      if (files.length) {
        const uploaded = await uploadService.uploadEventFiles(created.slug, files);
        const media = uploaded.map((f) => ({ url: f.url, label: f.label }));
        await eventService.updateEventMedia(created.slug, media);
      }

      onCreated?.(created);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <form onSubmit={onSubmit}>
        <DialogTitle sx={{ fontWeight: 600 }}>Nouvel évènement</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField name="title" label="Titre" fullWidth required />
            </Grid>

            <Grid item xs={12}>
              <TextField name="description" label="Description" fullWidth multiline minRows={4} />
            </Grid>

            {/* Début : date + heure */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Date de début"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={onStartDateChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Heure de début"
                type="time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                disabled={timeDisabled}
              />
            </Grid>

            {/* Fin : date + heure */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Date de fin"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={onEndDateChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Heure de fin"
                type="time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                disabled={timeDisabled}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox name="allDay" checked={allDay} onChange={onAllDayChange} />}
                label="Toute la journée"
              />
              {multiDay && (
                <Typography variant="caption" sx={{ ml: 1 }}>
                  (évènement multi-jours : heures désactivées)
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField name="city" label="Ville" fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField name="address" label="Adresse" fullWidth />
            </Grid>

            <Grid item xs={12}>
              <TextField name="organizerName" label="Organisateur" fullWidth />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField name="officialUrl" label="Site officiel" fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField name="email" label="Email de contact" type="email" fullWidth />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField name="price" label="Prix (€)" type="number" fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField name="author" label="Auteur" fullWidth defaultValue={defaultAuthor} />
            </Grid>

            {/* Médias */}
            <Grid item xs={12}>
              <Button variant="contained" component="label" sx={{ color: "common.white" }}>
                Sélectionner des fichiers (images / PDF)
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => setFiles(Array.from(e.target.files || []))}
                />
              </Button>
              {files.length > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {files.length} fichier(s) sélectionné(s)
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              bgcolor: "grey.700",
              color: "common.white",
              "&:hover": { bgcolor: "grey.800" },
            }}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disableElevation
            sx={{ ml: 1, minWidth: 160, color: "common.white" }}
            disabled={saving}
            endIcon={saving ? <CircularProgress size={18} sx={{ color: "common.white" }} /> : null}
          >
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

CreateEventDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
};
